import i18n, { type BackendModule, type ReadCallback, type ResourceKey } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import { postaviJezikZahtjeva } from '../api/client'
import en from './locales/en.json'

/**
 * i18n od prvog dana (CLAUDE.md §2, §13): NIJEDAN tekst ne stoji u JSX-u.
 * Rezervni jezik je `en` — resurs koji nedostaje pada na engleski, ne na ključ.
 *
 * Odabrani jezik je jedina stvar uz refresh token koja smije u `localStorage`
 * (§13), pa je detektor namjerno ograničen na tu jednu pohranu (uz jezik
 * preglednika kao prvu pretpostavku).
 *
 * **JEDAN JEZIK SE NOSI, DVA SE DOHVAĆAJU** (dotjerivanje-3 §4, mjereno).
 *
 * Sva tri rječnika stajala su u ULAZNOM chunku — 387 kB izvora, dakle preko
 * polovice svega što preglednik mora pročitati prije nego što se nacrta prvi
 * piksel. Gost je tako pri svakom prvom otvaranju plaćao dva jezika koja neće ni
 * vidjeti, a to se osjetilo na oba ekrana koja su prijavljena kao spora
 * (naslovnica i karta): oni ne čekaju svoje podatke nego aplikaciju.
 *
 * Zato ovdje stoji samo **rezervni** jezik (`en`), a hrvatski i njemački su
 * vlastiti chunkovi koje dohvaća sitni backend ispod. Rezervni ostaje ugrađen
 * namjerno: on je mreža za ključ koji negdje fali, a mreža koja se tek dohvaća
 * nije mreža.
 */

export const JEZICI = ['hr', 'en', 'de'] as const
export type Jezik = (typeof JEZICI)[number]

export const ZADANI_JEZIK: Jezik = 'en'
const KLJUC_JEZIKA = 'shuttly.lang'

/**
 * Rječnici koji se DOHVAĆAJU. Statični `import()` po jeziku, a ne izraz s
 * varijablom u putanji: alat mora u trenutku gradnje znati koji chunkovi
 * postoje — inače ih ili ne napravi ili napravi sve i uvuče natrag u ulazni.
 */
const LIJENI: Record<string, () => Promise<{ default: ResourceKey }>> = {
  hr: () => import('./locales/hr.json'),
  de: () => import('./locales/de.json'),
}

/**
 * Najmanji mogući backend: jedan `read`, bez mreže i bez konfiguracije.
 *
 * `i18next-http-backend` bi ovo obavio, ali uz vlastiti paket, vlastiti dohvat i
 * rječnike izvan builda — dakle datoteke bez hasha, koje bi trebalo posebno
 * keširati i posebno deployati (CLAUDE.md §15: bez novih paketa). Ovako su
 * rječnici i dalje dio builda, sa svime što iz toga slijedi.
 */
const LijeniRjecnici: BackendModule = {
  type: 'backend',
  init: () => {},
  read(jezik: string, _imenik: string, gotovo: ReadCallback) {
    // Ugrađeni (`en`) i nepoznat jezik odgovaraju ODMAH i prazno: prvi je već u
    // `resources`, drugi ne postoji — a backend koji ne odgovori drži `ready` na
    // `false` i s njim cijeli ekran.
    const ucitaj = LIJENI[jezik]

    if (!ucitaj) {
      gotovo(null, {})
      return
    }

    ucitaj()
      .then((modul) => gotovo(null, modul.default))
      // Pad dohvata NIJE prazan ekran: `en` je i dalje u memoriji, pa sučelje
      // ostaje na rezervnom jeziku umjesto da ostane bez teksta.
      .catch(() => gotovo(null, {}))
  },
}

/**
 * Kad je i18n spreman za crtanje. `main.tsx` ga čeka prije prvog `render`a —
 * inače bi gost s hrvatskim jezikom vidio kadar engleskog dok mu rječnik stigne.
 */
export const i18nSpreman = i18n
  .use(LijeniRjecnici)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Samo rezervni jezik ide u ulazni chunk; ostale donosi backend iznad.
    resources: { en: { translation: en } },
    partialBundledLanguages: true,
    supportedLngs: [...JEZICI],
    fallbackLng: ZADANI_JEZIK,
    // Bez ovoga bi 'hr-HR' tražio zaseban resurs i pao na engleski.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: KLJUC_JEZIKA,
      caches: ['localStorage'],
    },
    interpolation: {
      // React već štiti od injekcije; dvostruko izbjegavanje razbija dijakritiku.
      escapeValue: false,
    },
  })

// Poslužitelj lokalizira detalje grešaka po `Accept-Language` (API §0), pa jezik
// sučelja i jezik odgovora moraju biti isti — inače bi ekran bio na hrvatskom, a
// poruka o grešci na engleskom.
postaviJezikZahtjeva(i18n.resolvedLanguage ?? ZADANI_JEZIK)
i18n.on('languageChanged', (jezik) => {
  postaviJezikZahtjeva(jezik)
  document.documentElement.lang = jezik
})

export default i18n
